// src/pos-app/surfaces/waiter/FloorCanvas.js
//
// F12a-N Task W2 — the native 800x500 fit-to-content floor canvas (design
// contract: docs/mockups/f12a-n/waiter-tables.html .fcanvas/.fstage/.ftable
// + .legendrow + .modebar + .mergebar, JS buildFloor/fitFloor/openPop
// L927-1216).
// fix/floor-plan-polish: the stage is now positioned by the fit-to-content transform from components/pos/floorGeometry.js (see that file's header).
// Mounted by TablesSurface's floor column; owns tap→popover
// state and positioning (mirrors TableServicePage.js:840-846 position
// capture — narrowed to this canvas box instead of the whole floor column
// — and :316-363 handleTableTap resolution), but seat-mode / merge-mode
// STATE lives one level up in TablesSurface (task item 3) — this component
// only reports taps via onMergeToggle/onSeatTarget while those modes are
// active, and renders the mode banner / merge bar from props so DOM order
// matches the mockup exactly (legend, modebar, canvas).
//
// Status derivation: ONE shared helper — deriveTableStatus + STATUS_TOKENS,
// imported from ../cashier/TableSelect (the native surfaces' single
// status-derivation source, itself a faithful mirror of
// VisualTableSelector.getTableStatus — see that file's header). Merge-group
// resolution: VisualTableSelector's exported buildTableGroups/
// resolveOccupation, zero edits to that component. This also resolves the
// parity inventory's "duplicated status-derivation... must stay in sync"
// flag (TableServicePage.js:345-353 vs VisualTableSelector.js:204-227 used
// to disagree on the `cleared_at` / `occ.status==='active'` checks) — the
// native rebuild uses ONE function everywhere instead of reproducing the
// drift a third time.
// fix/floor-plan-polish: markers are floored at MIN_MARKER and carry a short
// status word (markerLabel); the legend keeps the full statusLabel.
//
// Table-layout parsing (tables_config -> {number,x,y,shape,size,capacity})
// and the seated-duration formatter are NOT exported by VisualTableSelector
// (they are closure-local), so they are faithfully extracted here:
//   parseTablesConfig <- VisualTableSelector.js:136-191 (getCurrentTables)
//   getSeatedDuration <- VisualTableSelector.js:43-51
// This is the first native copy — cashier's Visual mode reuses the actual
// <VisualTableSelector/> component wholesale, so there is no competing
// extraction to collide with. One deviation: `table.color` (used only by
// VisualTableSelector's legacy no-occupations fallback path, never taken
// here since occupations is always an array in the native surfaces) is not
// parsed — dead weight for this rebuild, not a behavior change.
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { buildTableGroups, resolveOccupation } from '../../../components/pos/VisualTableSelector';
import { deriveTableStatus, STATUS_TOKENS } from '../cashier/TableSelect';
import TablePopover from './TablePopover';
import { fitFloorToBox, floorTransform } from '../../../components/pos/floorGeometry';

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 500;
const POPOVER_W = 236;
const POPOVER_H = 260;
const POPOVER_MARGIN = 8;

const STATUS_ORDER = ['available', 'seated', 'ordered', 'awaiting_bill', 'paid'];

// Faithful extraction — VisualTableSelector.js:43-51 (getSeatedDuration).
export const getSeatedDuration = (seatedAt) => {
    if (!seatedAt) return '';
    const mins = Math.floor((Date.now() - new Date(seatedAt).getTime()) / 60000);
    if (mins <= 0) return '<1m';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem > 0 ? `${hrs}h${rem}m` : `${hrs}h`;
};

// Hard minimum marker size in stage space. Below this the status word cannot
// fit the circle's chord (the clipping bug this branch fixes); tenant_demo's
// tables are 100px apart centre-to-centre so ≥16px gaps remain at 84.
export const MIN_MARKER = 84;

// Faithful extraction — VisualTableSelector.js:136-191 (getCurrentTables),
// parameterized on the single dining option's tables_config (the caller —
// TablesSurface — already resolves selectedDiningOption -> tablesConfig,
// mirroring `diningOptions[selectedDiningOption]` there).
export const parseTablesConfig = (tablesConfig) => {
    if (!tablesConfig) return [];
    if (Array.isArray(tablesConfig)) {
        return tablesConfig
            .filter((table) => typeof table === 'object' && table.number !== undefined)
            .map((table) => ({
                id: `table-${table.number}`,
                number: table.number,
                x: table.x || 200,
                y: table.y || 200,
                shape: table.shape || 'circle',
                size: Math.max(table.size || 60, MIN_MARKER),
                capacity: table.capacity || 4,
            }));
    }
    if (typeof tablesConfig === 'object') {
        return Object.entries(tablesConfig).map(([num, design]) => {
            const d = Array.isArray(design) && design.length > 0 ? design[0] : (design || {});
            const idx = parseInt(num, 10) - 1;
            let px = d.x;
            let py = d.y;
            // Percentage coordinates (0-1) stored in the DB convert to the
            // fixed 800x500 pixel canvas; already-pixel values pass through.
            if (px !== undefined && px <= 1) px = Math.round(px * CANVAS_WIDTH);
            if (py !== undefined && py <= 1) py = Math.round(py * CANVAS_HEIGHT);
            return {
                id: `table-${num}`,
                number: parseInt(num, 10),
                x: px || 100 + (idx % 5) * 140,
                y: py || 100 + Math.floor(idx / 5) * 120,
                shape: d.shape || 'circle',
                size: Math.max(d.size || 60, MIN_MARKER),
                capacity: d.capacity || 4,
            };
        }).sort((a, b) => a.number - b.number);
    }
    return [];
};

const UsersIcon = (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
    </svg>
);

const ModeIcon = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20" />
    </svg>
);

const CSS = `
.wtb-floorwrap{flex:1;min-height:0;display:flex;flex-direction:column;overflow:hidden;}

/* status legend — mockup .legendrow / .leg */
.wtb-legend{flex:none;display:flex;align-items:center;gap:14px;flex-wrap:wrap;margin-bottom:8px;min-height:20px;}
.wtb-leg{display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:700;
  text-transform:uppercase;letter-spacing:.4px;}
.wtb-leg i{width:9px;height:9px;border-radius:50%;flex:none;}

/* mode banner — mockup .modebar */
.wtb-modebar{flex:none;display:flex;align-items:center;gap:10px;padding:9px 14px;margin-bottom:8px;
  background:var(--c-greenLight);border:1px solid var(--c-greenBorder);border-radius:12px;
  color:var(--c-ink);font-size:13px;font-weight:600;}
.wtb-modebar svg{color:var(--c-green);flex:none;}
.wtb-modebar-grow{flex:1;min-width:0;}
.wtb-modebar-cancel{min-height:30px;padding:0 12px;border-radius:9px;font-weight:700;font-size:12px;
  background:var(--c-surface);border:1px solid var(--c-greenBorder);color:var(--c-green);cursor:pointer;
  font-family:inherit;}

/* canvas box — a plain floor surface, not the editor's ruled canvas */
.wtb-fbox{position:relative;flex:1;min-height:0;border-radius:16px;background:var(--c-faint);
  border:1px solid var(--c-border);overflow:hidden;}
/* 800x500 stage, positioned by the fit-to-content transform (floorGeometry.js) */
.wtb-fstage{position:absolute;left:0;top:0;width:800px;height:500px;transform-origin:0 0;}
.wtb-flinks{position:absolute;inset:0;pointer-events:none;}
.wtb-flinks line{stroke:var(--c-green);stroke-width:2.5;stroke-dasharray:6 5;opacity:.55;}

/* table tiles — mockup .ftable. The button is the marker outline + chip
   anchor; its text lives in .wtb-fbody, which clips to the marker shape so
   nothing can ever spill past the ring, while the chips (.wtb-dur,
   .wtb-grpbadge, .wtb-selbadge) stay siblings and hang outside on purpose. */
.wtb-ftable{position:absolute;background:var(--c-surface);border:2px solid var(--ft, var(--c-success));
  box-shadow:0 4px 12px rgba(2,8,23,.12);font-family:inherit;font-weight:800;color:var(--c-ink);
  cursor:pointer;transition:transform .12s, box-shadow .12s;padding:0;}
.wtb-fbody{position:absolute;inset:0;border-radius:inherit;overflow:hidden;box-sizing:border-box;padding:4px;
  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;}
.wtb-fnum{font-size:15px;line-height:1;}
.wtb-ftable:hover{transform:scale(1.04);z-index:3;}
.wtb-ftable.round{border-radius:50%;}
.wtb-ftable.rect{border-radius:14px;}
.wtb-ftable.selected{border-style:dashed;box-shadow:0 0 0 4px var(--c-greenLight), 0 4px 12px rgba(2,8,23,.12);}
.wtb-fs{display:flex;align-items:center;gap:4px;font-size:9px;font-weight:800;text-transform:uppercase;
  letter-spacing:.3px;white-space:nowrap;max-width:100%;overflow:hidden;text-overflow:ellipsis;}
.wtb-fs i{width:7px;height:7px;border-radius:50%;flex:none;}
.wtb-fparty{display:flex;align-items:center;gap:4px;font-size:10px;font-weight:700;color:var(--c-muted);}
.wtb-dur{position:absolute;top:-11px;left:50%;transform:translateX(-50%);font-size:9.5px;font-weight:800;
  padding:2px 8px;border-radius:999px;white-space:nowrap;background:var(--c-surface);border:1.5px solid var(--ft);
  color:var(--c-ink);box-shadow:0 2px 6px rgba(2,8,23,.14);z-index:2;}
.wtb-grpbadge{position:absolute;top:-11px;right:-8px;font-size:9px;font-weight:800;letter-spacing:.4px;
  padding:2px 8px;border-radius:999px;white-space:nowrap;background:var(--c-green);color:#fff;z-index:2;}
[dir="rtl"] .wtb-grpbadge{right:auto;left:-8px;}
.wtb-memlabel{font-size:9.5px;font-weight:800;color:var(--c-green);}
.wtb-servedchip{font-size:8.5px;font-weight:800;letter-spacing:.5px;padding:1px 6px;border-radius:6px;
  color:var(--c-success);background:var(--c-greenLight);border:1px solid var(--c-greenBorder);}
.wtb-selbadge{position:absolute;top:-12px;left:-8px;min-width:24px;height:24px;padding:0 7px;border-radius:999px;
  background:var(--c-green);color:#fff;font-size:11px;font-weight:800;display:flex;align-items:center;justify-content:center;
  z-index:4;}
[dir="rtl"] .wtb-selbadge{left:auto;right:-8px;}

/* popover backdrop — click-outside close */
.wtb-pop-backdrop{position:absolute;inset:0;z-index:15;}

/* merge confirm bar — mockup .mergebar */
.wtb-mergebar{position:absolute;left:12px;right:12px;bottom:12px;z-index:6;display:flex;align-items:center;gap:10px;
  padding:10px 14px;background:color-mix(in srgb, var(--c-surface) 90%, transparent);border:1px solid var(--c-border);
  border-radius:14px;box-shadow:0 10px 30px rgba(2,8,23,.18);font-size:12.5px;font-weight:700;
  -webkit-backdrop-filter:blur(18px) saturate(1.5);backdrop-filter:blur(18px) saturate(1.5);}
.wtb-mergebar-grow{flex:1;min-width:0;}
.wtb-mergebar-hint{display:block;font-size:10.5px;font-weight:600;color:var(--c-muted);}
.wtb-mb-btn{min-height:36px;padding:0 15px;border-radius:10px;font-size:12.5px;font-weight:800;cursor:pointer;
  font-family:inherit;border:1px solid var(--c-border);background:var(--c-surface);color:var(--c-slate);}
.wtb-mb-btn.confirm{background:var(--c-green);color:#fff;border-color:var(--c-green);}
.wtb-mb-btn.confirm:disabled{opacity:.45;cursor:not-allowed;}
`;

// F12a-N Task W5 — forwardRef + `onPopoverOpenChange` (Escape-chain
// centralization, item 2 of the plan). The popover's open/closed state
// stays local to this component (unchanged — tap position math lives here
// too), but TablesSurface's single keydown authority needs to (a) know
// whether the popover is currently the topmost overlay and (b) be able to
// dismiss it on Escape without owning its state. `onPopoverOpenChange` fires
// whenever `popover` flips null<->non-null; `closePopover` is exposed via
// `useImperativeHandle` so the parent can call it imperatively, the same way
// the backdrop-click handler already does internally.
const FloorCanvas = forwardRef(function FloorCanvas({
    diningOptionName,
    tablesConfig,
    occupations,
    pendingOrders,
    mergeMode,
    mergeSelection,
    onMergeToggle,
    onMergeCancel,
    onMergeConfirm,
    seatMode,
    onSeatTarget,
    onCancelSeatMode,
    onSeatWalkIn,
    onNewOrder,
    onAddItems,
    onMarkServed,
    onRequestBill,
    onChangeTable,
    onSplitGroup,
    onClearTable,
    onPopoverOpenChange,
}, ref) {
    const { t } = useLanguage();
    const canvasBoxRef = useRef(null);
    const [fit, setFit] = useState(() => fitFloorToBox([], 0, 0));
    const [popover, setPopover] = useState(null);
    const [walkinPartySize, setWalkinPartySize] = useState(2);

    const tables = useMemo(() => parseTablesConfig(tablesConfig), [tablesConfig]);
    const tableByNumber = useMemo(() => {
        const m = {};
        tables.forEach((tt) => { m[String(tt.number)] = tt; });
        return m;
    }, [tables]);
    const { groupByAnchor, anchorByMember } = useMemo(
        () => buildTableGroups(occupations),
        [occupations]
    );
    const mergeSelStr = (mergeSelection || []).map(String);

    // Fit-to-content (floorGeometry.js): the markers' bounding box fills the
    // box, upscaling allowed. Replaces the old whole-stage min(w/800,h/500,1).
    const recalcFit = useCallback(() => {
        const el = canvasBoxRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        setFit(fitFloorToBox(tables, rect.width, rect.height));
    }, [tables]);

    useEffect(() => {
        const id = setTimeout(recalcFit, 50);
        return () => clearTimeout(id);
    }, [recalcFit, diningOptionName, !!seatMode, mergeMode]);

    useEffect(() => {
        window.addEventListener('resize', recalcFit);
        return () => window.removeEventListener('resize', recalcFit);
    }, [recalcFit]);

    // Observe the box itself: the window 'resize' event misses container-
    // driven reflows (side-panel breakpoint, iOS rotation intermediates), and
    // a stale rect now shifts the fit off-centre rather than merely mis-
    // scaling it. Window listener stays as the fallback (jsdom, old WebViews).
    useEffect(() => {
        const el = canvasBoxRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => recalcFit());
        ro.observe(el);
        return () => ro.disconnect();
    }, [recalcFit]);

    // Closing the popover whenever a mode starts mirrors legacy's mutual
    // exclusivity (TableServicePage.js:498-503, :328-332).
    useEffect(() => {
        if (mergeMode || seatMode) setPopover(null);
    }, [mergeMode, seatMode]);

    // Keep in sync with markerLabel below — both index the same status set.
    const statusLabel = (status) => ({
        available: t('available'),
        seated: t('seated'),
        ordered: t('ordered', {}, 'Ordered'),
        awaiting_bill: t('awaiting_bill'),
        paid: t('paid'),
    }[status]);

    // Short forms for the marker itself — the legend and aria-labels keep the
    // full words. `free` (existing key) is not reused: its Arabic means "free
    // of charge", not "unoccupied".
    // Keep in sync with statusLabel above.
    const markerLabel = (status) => ({
        available: t('table_status_short_available', {}, 'Free'),
        seated: t('seated'),
        ordered: t('ordered', {}, 'Ordered'),
        awaiting_bill: t('table_status_short_awaiting_bill', {}, 'Bill'),
        paid: t('paid'),
    }[status]);

    // Faithful extraction — TableServicePage.js:316-363 (handleTableTap):
    // merge/seat modes act on the RAW tapped table (no anchor resolution —
    // matches legacy exactly); the normal path resolves a merged member's
    // tap to its anchor occupation before deriving status and opening the
    // popover. Position math follows the mockup's openPop (L1107-1120):
    // clamp against the canvas box's own rect, not the legacy page's cruder
    // floor-column clamp.
    const handleTileTap = (e, rawNumStr) => {
        if (mergeMode) { onMergeToggle(rawNumStr); return; }
        if (seatMode) { onSeatTarget(rawNumStr); return; }

        const occ = resolveOccupation(occupations, rawNumStr);
        const resolvedNum = occ ? String(occ.table_number) : rawNumStr;
        const tableKey = `${diningOptionName}-${resolvedNum}`;
        const hasPendingOrder = !!(pendingOrders && pendingOrders[tableKey]);
        const status = deriveTableStatus(occ, hasPendingOrder);
        const table = tableByNumber[resolvedNum];

        const rect = canvasBoxRef.current ? canvasBoxRef.current.getBoundingClientRect() : { left: 0, top: 0, width: CANVAS_WIDTH, height: CANVAS_HEIGHT };
        const rawX = e.clientX - rect.left + POPOVER_MARGIN;
        const rawY = e.clientY - rect.top + POPOVER_MARGIN;
        const left = Math.max(POPOVER_MARGIN, Math.min(rawX, rect.width - POPOVER_W - POPOVER_MARGIN));
        const top = Math.max(POPOVER_MARGIN, Math.min(rawY, rect.height - POPOVER_H - POPOVER_MARGIN));

        setWalkinPartySize(2);
        setPopover({
            tableNumber: resolvedNum,
            status,
            occupation: occ || null,
            capacity: table ? table.capacity : 4,
            isGroupAnchor: !!groupByAnchor[resolvedNum],
            left,
            top,
        });
    };

    const closePopover = () => setPopover(null);

    // Report open/closed state up + expose an imperative close (Task W5
    // Escape-chain centralization — see header note).
    useEffect(() => {
        if (onPopoverOpenChange) onPopoverOpenChange(!!popover);
    }, [popover, onPopoverOpenChange]);

    useImperativeHandle(ref, () => ({ closePopover }), []);

    // Coordinate lookup for the merged-group SVG connectors — VTS:382-398.
    const tableCoords = {};
    tables.forEach((tt) => { tableCoords[String(tt.number)] = tt; });

    return (
        <div className="wtb-floorwrap" data-zone="floor">
            <style>{CSS}</style>

            <div className="wtb-legend" aria-label={t('table_status_legend', {}, 'Table status legend')}>
                {STATUS_ORDER.map((status) => (
                    <span key={status} className="wtb-leg" style={{ color: STATUS_TOKENS[status] }}>
                        <i style={{ background: STATUS_TOKENS[status] }} />
                        {statusLabel(status)}
                    </span>
                ))}
            </div>

            {seatMode && (
                <div className="wtb-modebar" role="status">
                    {ModeIcon}
                    <span className="wtb-modebar-grow">{seatMode.label}</span>
                    <button type="button" className="wtb-modebar-cancel" onClick={onCancelSeatMode}>
                        {t('cancel')}
                    </button>
                </div>
            )}

            <div
                className="wtb-fbox"
                ref={canvasBoxRef}
                style={{ cursor: seatMode ? 'crosshair' : 'default' }}
            >
                <div className="wtb-fstage" style={{ transform: floorTransform(fit) }}>
                    <svg className="wtb-flinks" width={CANVAS_WIDTH} height={CANVAS_HEIGHT} aria-hidden="true">
                        {Object.entries(groupByAnchor).map(([anchorNum, group]) => {
                            const a = tableCoords[anchorNum];
                            if (!a) return null;
                            return group.members.map((memberNum) => {
                                const m = tableCoords[memberNum];
                                if (!m) return null;
                                return <line key={`link-${anchorNum}-${memberNum}`} x1={a.x} y1={a.y} x2={m.x} y2={m.y} />;
                            });
                        })}
                    </svg>

                    {tables.map((table) => {
                        const numStr = String(table.number);
                        const tableKey = `${diningOptionName}-${numStr}`;
                        const occ = resolveOccupation(occupations, numStr);
                        const hasPendingOrder = !!(pendingOrders && pendingOrders[tableKey]);
                        const status = deriveTableStatus(occ, hasPendingOrder);
                        const color = STATUS_TOKENS[status];
                        const groupHere = groupByAnchor[numStr];
                        const memberOf = anchorByMember[numStr];
                        const isMember = !!memberOf && !groupHere;
                        const seatedDuration = occ && !isMember ? getSeatedDuration(occ.seated_at) : '';
                        const isMergeSelected = mergeMode && mergeSelStr.includes(numStr);
                        const selIndex = mergeSelStr.indexOf(numStr);

                        return (
                            <button
                                key={table.id}
                                type="button"
                                className={`wtb-ftable ${table.shape === 'circle' ? 'round' : 'rect'}${isMergeSelected ? ' selected' : ''}`}
                                data-table={numStr}
                                data-status={status}
                                style={{
                                    left: table.x - table.size / 2,
                                    top: table.y - table.size / 2,
                                    width: table.size,
                                    height: table.size,
                                    '--ft': color,
                                }}
                                onClick={(e) => handleTileTap(e, numStr)}
                                aria-label={`${t('table')} ${numStr} — ${statusLabel(status)}`}
                            >
                                {seatedDuration && <span className="wtb-dur">{seatedDuration}</span>}
                                {groupHere && (
                                    <span className="wtb-grpbadge">
                                        {t('group')} · {occ ? occ.party_size : groupHere.members.length + 1}
                                    </span>
                                )}
                                <span className="wtb-fbody">
                                    <span className="wtb-fnum">{numStr}</span>
                                    {isMember ? (
                                        <span className="wtb-memlabel">↳ {occ.table_number}</span>
                                    ) : (
                                        <span className="wtb-fs" style={{ color }}>
                                            <i style={{ background: color }} />
                                            {markerLabel(status)}
                                        </span>
                                    )}
                                    {occ?.served_at && !isMember && <span className="wtb-servedchip">{t('served')}</span>}
                                    <span className="wtb-fparty">
                                        {UsersIcon}
                                        {isMember ? '' : (occ ? `${occ.party_size}/${table.capacity}` : `${table.capacity}`)}
                                    </span>
                                </span>
                                {isMergeSelected && (
                                    <span className="wtb-selbadge">{selIndex === 0 ? '1 · A' : String(selIndex + 1)}</span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {popover && <div className="wtb-pop-backdrop" onClick={closePopover} />}
                {popover && (
                    <TablePopover
                        table={popover}
                        partySize={walkinPartySize}
                        onPartySizeChange={setWalkinPartySize}
                        onClose={closePopover}
                        onSeatWalkIn={() => { onSeatWalkIn(popover.tableNumber, walkinPartySize); closePopover(); }}
                        onNewOrder={() => { onNewOrder(popover.occupation); closePopover(); }}
                        onAddItems={() => { onAddItems(popover.occupation); closePopover(); }}
                        onMarkServed={() => { onMarkServed(popover.occupation); closePopover(); }}
                        onRequestBill={() => { onRequestBill(popover.occupation); closePopover(); }}
                        onChangeTable={() => { onChangeTable(popover.occupation); closePopover(); }}
                        onSplitGroup={() => { onSplitGroup(popover.occupation); closePopover(); }}
                        onClearTable={() => { onClearTable(popover.occupation); closePopover(); }}
                    />
                )}

                {mergeMode && (
                    <div className="wtb-mergebar">
                        <span className="wtb-mergebar-grow">
                            {mergeSelStr.length === 0
                                ? t('tap_tables_to_merge', {}, 'Tap tables to merge — first pick is the anchor')
                                : `${t('anchor', {}, 'Anchor')} ${mergeSelStr[0]}${mergeSelStr.length > 1 ? ` + ${mergeSelStr.slice(1).join(', ')}` : ''}`}
                            <span className="wtb-mergebar-hint">
                                {t('merge_requires_online_min_two', {}, 'Requires online · minimum 2 tables · member bills fold into the anchor')}
                            </span>
                        </span>
                        <button type="button" className="wtb-mb-btn" onClick={onMergeCancel}>{t('cancel')}</button>
                        <button
                            type="button"
                            className="wtb-mb-btn confirm"
                            disabled={mergeSelStr.length < 2}
                            onClick={onMergeConfirm}
                        >
                            {mergeSelStr.length < 2 ? t('confirm_merge', {}, 'Merge') : `${t('confirm_merge', {}, 'Merge')} ${mergeSelStr.length}`}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
});

export default FloorCanvas;
